import React, { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import * as LuIcons from 'react-icons/lu';
const { LuMenu, LuX, LuSun, LuMoon, LuLock, LuGlobe } = LuIcons;
import SafeIcon from './common/SafeIcon';
import { NavLink } from '../types';
import { Button3D } from './UiElements';
import { useLanguage } from '../contexts/LanguageContext';

interface NavbarProps {
  links: NavLink[];
  isDark: boolean;
  toggleTheme: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ links, isDark, toggleTheme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const toggleLanguage = () => {
    setLanguage(language === 'fr' ? 'en' : 'fr');
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 dark:bg-be-darkest/95 backdrop-blur-md shadow-lg py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <RouterLink to="/" className="flex items-center gap-2 group">
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1775921981458-Logo%20BE%20consulting%20final.PNG" 
              alt="BE Consulting Logo" 
              className="h-12 w-auto object-contain"
              fetchpriority="high"
              decoding="async" 
            />
          </RouterLink>

          <div className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <RouterLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-semibold uppercase tracking-wide hover:text-be-france transition-colors ${
                    isActive ? 'text-be-france' : 'text-gray-700 dark:text-gray-200'
                  }`
                }
              >
                {link.label}
              </RouterLink>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button onClick={toggleLanguage} className="p-2 flex items-center gap-1 font-semibold text-sm text-gray-700 dark:text-gray-200 hover:text-be-france transition-colors">
              <SafeIcon icon={LuGlobe} size={18} />
              {language.toUpperCase()}
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              {isDark ? <SafeIcon icon={LuSun} size={20} className="text-yellow-400" /> : <SafeIcon icon={LuMoon} size={20} className="text-be-night" />}
            </button>
            <RouterLink to="/admin" className="p-2 text-gray-400 hover:text-be-france transition-colors" title={t('adminArea')}>
              <SafeIcon icon={LuLock} size={18} />
            </RouterLink>
            <a href="tel:+237678306248">
              {/* Bouton CTA doublé de volume avec des marges et une police plus grande */}
              <Button3D variant="primary" className="px-6 py-3 text-lg">{t('callNow')}</Button3D>
            </a>
          </div>

          <div className="md:hidden flex items-center gap-4">
            <button onClick={toggleLanguage} className="p-1 font-semibold text-sm text-gray-700 dark:text-gray-200">{language.toUpperCase()}</button>
            <button onClick={toggleTheme} className="p-1">
              {isDark ? <SafeIcon icon={LuSun} size={20} className="text-yellow-400" /> : <SafeIcon icon={LuMoon} size={20} />}
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-be-darkest dark:text-white">
              {isOpen ? <SafeIcon icon={LuX} size={28} /> : <SafeIcon icon={LuMenu} size={28} />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white dark:bg-be-darkest border-t dark:border-gray-800 shadow-xl p-4 flex flex-col gap-4">
            {links.map((link) => (
              <RouterLink
                key={link.path}
                to={link.path}
                className="text-lg font-semibold text-center py-2 border-b border-gray-100 dark:border-gray-800"
              >
                {link.label}
              </RouterLink>
            ))}
            <RouterLink to="/admin" className="text-lg font-bold text-center py-2 flex items-center justify-center gap-2 text-be-france">
              <SafeIcon icon={LuLock} size={18} /> {t('adminArea')}
            </RouterLink>
            <a href="tel:+237678306248" className="mt-2">
              <Button3D variant="primary" className="py-4 text-lg" fullWidth>{t('callNow')}</Button3D>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;